'use client';

import FadeIn from '../ui/FadeIn';
import { FileSpreadsheet, FileText, User, Activity, Database } from 'lucide-react';
import { useTranslations } from '@/lib/i18n/TranslationProvider';

export default function ExportFeatures() {
    const { t } = useTranslations();
    return (
        <section className="section-padding bg-[var(--color-md-surface)] relative overflow-hidden">
            <div className="container mx-auto px-6 max-w-6xl">

                {/* Section Header */}
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <FadeIn>
                        <h2 className="text-3xl md:text-5xl font-black text-[var(--color-md-on-surface)] tracking-tight mb-6 leading-tight">
                            {t.exportFeatures.heading}<br />
                            <span className="text-[var(--color-md-primary)]">{t.exportFeatures.headingAccent}</span>
                        </h2>
                        <p className="text-lg text-[var(--color-md-on-surface-variant)] leading-relaxed">
                            {t.exportFeatures.subtitle}
                        </p>
                    </FadeIn>
                </div>

                {/* Bento Grid layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* Block 1: The Core Value (Spans 2 columns on large screens) */}
                    <div className="lg:col-span-2">
                        <FadeIn delay={0.1} className="h-full">
                            <div className="h-full rounded-3xl p-8 md:p-12 bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline-variant)] shadow-cinematic-2 flex flex-col md:flex-row items-center gap-10 overflow-hidden relative group">

                                <div className="z-10 flex-1">
                                    <h3 className="text-2xl font-bold text-[var(--color-md-on-surface)] mb-4">{t.exportFeatures.block1Title}</h3>
                                    <p className="text-[var(--color-md-on-surface-variant)] leading-relaxed text-[15px] mb-8">
                                        {t.exportFeatures.block1Desc}
                                    </p>

                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-[var(--color-md-on-surface)] text-sm font-medium">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)]" />
                                            {t.exportFeatures.block1Bullet1}
                                        </div>
                                        <div className="flex items-center gap-3 text-[var(--color-md-on-surface)] text-sm font-medium">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)]" />
                                            {t.exportFeatures.block1Bullet2}
                                        </div>
                                        <div className="flex items-center gap-3 text-[var(--color-md-on-surface)] text-sm font-medium">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)]" />
                                            {t.exportFeatures.block1Bullet3}
                                        </div>
                                    </div>
                                </div>

                                {/* Exact replica of extension's format selector UI */}
                                <div className="z-10 w-full md:w-[320px] shrink-0 bg-[var(--color-md-surface)] rounded-2xl p-5 border border-[var(--color-md-outline)] shadow-cinematic-1 transform transition-transform duration-500 group-hover:scale-105 group-hover:-rotate-1">
                                    <div className="space-y-3">
                                        {/* Excel Option - Selected */}
                                        <div className="flex items-center gap-4 p-3 rounded-xl bg-[var(--color-md-primary-container)] border border-[var(--color-md-primary)]/40 shadow-sm">
                                            <div className="w-10 h-10 m3-icon-tonal flex-shrink-0">
                                                <FileSpreadsheet className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-bold text-[var(--color-md-on-primary-container)] text-sm">Excel (.xlsx)</div>
                                                <div className="text-[11px] text-[var(--color-md-on-primary-container)]/70">Best for CRM analysis</div>
                                            </div>
                                            <div className="w-4 h-4 rounded-full border-2 border-[var(--color-md-primary)] bg-[var(--color-md-primary)] flex items-center justify-center shadow-sm">
                                                <div className="w-2 h-2 rounded-full bg-[var(--color-md-surface)]"></div>
                                            </div>
                                        </div>

                                        {/* CSV Option */}
                                        <div className="flex items-center gap-4 p-3 rounded-xl bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline-variant)] opacity-80">
                                            <div className="w-10 h-10 m3-icon-secondary flex-shrink-0">
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-bold text-[var(--color-md-on-surface)] text-sm">CSV</div>
                                            </div>
                                        </div>

                                        {/* VCF Option */}
                                        <div className="flex items-center gap-4 p-3 rounded-xl bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline-variant)] opacity-80">
                                            <div className="w-10 h-10 m3-icon-tertiary flex-shrink-0">
                                                <User className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-bold text-[var(--color-md-on-surface)] text-sm">VCard</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Aesthetic Glow */}
                                {/* Pure Flat Design: No Ambient Orbs */}
                            </div>
                        </FadeIn>
                    </div>

                    {/* Block 2 & 3 Column (Stack on right, or wrap on mobile) */}
                    <div className="lg:col-span-1 flex flex-col gap-6">

                        {/* Block 2: The Tracking USP */}
                        <FadeIn delay={0.2} className="flex-1">
                            <div className="h-full rounded-3xl p-8 bg-[var(--color-md-primary-container)] border border-[var(--color-md-outline-variant)] shadow-sm relative overflow-hidden group">
                                <div className="absolute -right-4 -top-4 w-24 h-24 bg-[var(--color-md-primary)]/10 rounded-full blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />

                                <div className="w-12 h-12 m3-icon-tertiary mb-6 relative z-10">
                                    <Activity strokeWidth={2.5} className="w-6 h-6" />
                                </div>

                                <h3 className="text-xl font-bold text-[var(--color-md-on-primary-container)] mb-3 relative z-10">{t.exportFeatures.block2Title}</h3>
                                <p className="text-[var(--color-md-on-primary-container)]/80 leading-relaxed text-[15px] relative z-10 m-0">
                                    {t.exportFeatures.block2Desc}
                                </p>
                            </div>
                        </FadeIn>

                        {/* Block 3: The Memory USP */}
                        <FadeIn delay={0.3} className="flex-1">
                            <div className="h-full rounded-3xl p-8 bg-[var(--color-md-secondary-container)] border border-[var(--color-md-outline)] shadow-sm relative overflow-hidden group hover:border-[var(--color-md-primary)]/30 transition-colors duration-500">
                                <div className="absolute right-0 bottom-0 w-32 h-32 bg-[var(--color-md-primary)]/10 rounded-tl-full blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />

                                <div className="w-12 h-12 m3-icon-tonal mb-6 relative z-10">
                                    <Database strokeWidth={2.5} className="w-6 h-6" />
                                </div>

                                <h3 className="text-xl font-bold text-[var(--color-md-on-secondary-container)] mb-3 relative z-10">{t.exportFeatures.block3Title}</h3>
                                <p className="text-[var(--color-md-on-surface-variant)] leading-relaxed text-[15px] relative z-10 m-0">
                                    {t.exportFeatures.block3Desc}
                                </p>
                            </div>
                        </FadeIn>

                    </div>

                </div>
            </div>
        </section>
    );
}
